import { useEffect, useState, useRef, useCallback } from "react";
import { IEvidence, CPDActivityType, EvidenceCustomTag, IDateRange } from "@interfaces/Interfaces";
import DataController from "@controllers/DataController";

interface UseAIEvidencePreviewParams {
  title: string;
  date: Date;
  activity: CPDActivityType | null;
  numberOfHours: string;
  activityDescription: string;
  reflections: string;
  benefits: string;
  objectives: string;
  customTags: EvidenceCustomTag[];
  activityDateMode: "single" | "range";
  activitySingleDate: Date;
  activityDateRange: IDateRange | undefined;
  addToHCPCAudit: boolean;
}

export const useAIEvidencePreview = (params: UseAIEvidencePreviewParams) => {
  const [latestEvidencePreview, setLatestEvidencePreview] = useState<IEvidence | null>(null);
  const latestEvidencePreviewRef = useRef<IEvidence | null>(null);

  const {
    title,
    date,
    activity,
    numberOfHours,
    activityDescription,
    reflections,
    benefits,
    objectives,
    customTags,
    activityDateMode,
    activitySingleDate,
    activityDateRange,
    addToHCPCAudit,
  } = params;

  // Keep ref in sync with state
  useEffect(() => {
    latestEvidencePreviewRef.current = latestEvidencePreview;
  }, [latestEvidencePreview]);

  // Update latestEvidencePreview when evidence fields are edited
  useEffect(() => {
    if (!latestEvidencePreviewRef.current) return;

    setLatestEvidencePreview((prevPreview) => {
      if (!prevPreview) return prevPreview;

      const updatedPreview: IEvidence = {
        ...prevPreview,
        title: title || undefined,
        date: date ? date.toISOString() : prevPreview.date,
        activity: activity || undefined,
        numberOfHours:
          numberOfHours && numberOfHours.length > 0 ? DataController.convertToNumberOfHours(numberOfHours) : undefined,
        activityDescription: activityDescription || undefined,
        reflections: reflections || undefined,
        benefits: benefits || undefined,
        objectives: objectives || undefined,
        customTags: customTags.length > 0 ? customTags : undefined,
        addToHCPCAudit: addToHCPCAudit,
      };

      // Handle activity date based on mode
      if (activityDateMode === "range" && activityDateRange?.to) {
        updatedPreview.activityDate = activityDateRange.from.toISOString();
        updatedPreview.activityDateEnd = activityDateRange.to.toISOString();
      } else if (activityDateMode === "single" && activitySingleDate) {
        updatedPreview.activityDate = activitySingleDate.toISOString();
        updatedPreview.activityDateEnd = undefined;
      }

      return updatedPreview;
    });
  }, [
    title,
    date,
    activity,
    numberOfHours,
    activityDescription,
    reflections,
    benefits,
    objectives,
    customTags,
    activityDateMode,
    activitySingleDate,
    activityDateRange,
    addToHCPCAudit,
  ]);

  const resetLatestEvidencePreview = useCallback(() => {
    setLatestEvidencePreview(null);
    latestEvidencePreviewRef.current = null;
  }, []);

  return {
    latestEvidencePreview,
    setLatestEvidencePreview,
    resetLatestEvidencePreview,
  };
};
